import { useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';

import { Modal } from '~components/Modals/Base';
import type { TModalProps } from '~components/Modals/types';
import { SubHeadline } from '~components/Typography';
import { useTranslations } from '~i18n/hooks';
import { usePathname, useRouter } from '~i18n/navigation';
import CloseIcon from '~public/assets/icons/close.svg';
import { ButtonIconGhost } from '~shared/ui/atoms/Buttons/ButtonGhost/Icon';
import { ButtonPrimary } from '~shared/ui/atoms/Buttons/ButtonPrimary';
import { InputField } from '~shared/ui/organisms/InputField';
import { updateQueryParamsWithPathname } from '~utils/searchParams';

import {
	CUSTOM_VALUE_QUERY_PARAM_PREFIX,
	CUSTOM_VALUE_QUERY_PARAM_VALUE,
} from '../../model/constants';
import type { BotWizardField } from '../../model/types';
import { ModalWrapper } from '../Field/styles';

export const CustomValueModal = ({
	open,
	onClose,
	locked,
	field,
}: TModalProps & {
	field: BotWizardField;
}) => {
	const [value, setValue] = useState<string>('');
	const [error, setError] = useState<string>('');
	const router = useRouter();
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const t = useTranslations('BotWizard.customValueModal');

	const onSubmit = () => {
		const parsedValue = parseInt(value);
		if (
			!field.isAgeCustom ||
			(Number.isFinite(parsedValue) && parsedValue >= 18 && parsedValue < 1000)
		) {
			router.push(
				updateQueryParamsWithPathname(
					{
						[CUSTOM_VALUE_QUERY_PARAM_PREFIX + field.queryKey]: value,
						[field.queryKey]: CUSTOM_VALUE_QUERY_PARAM_VALUE,
					},
					pathname,
					searchParams
				),
				{ scroll: false }
			);
			setValue('');
			onClose();
		} else {
			setError(t('ageError'));
		}
	};

	useEffect(() => {
		return () => {
			setValue('');
			setError('');
		};
	}, [open]);
	return (
		<Modal locked={locked} open={open} onClose={onClose}>
			<ModalWrapper>
				<ButtonIconGhost onClick={onClose} size="L" leftIcon={<CloseIcon />} />
				<SubHeadline size="M">
					{t('modalTitle')} {field.title.toLowerCase()}
				</SubHeadline>
				<InputField
					maxLength={30}
					type={field.isAgeCustom ? 'number' : 'text'}
					error={error}
					autoFocus={true}
					value={value}
					onChange={(e) =>
						setValue(
							field.isAgeCustom
								? e.target.value.replace(/\D/g, '')
								: e.target.value
						)
					}
					uiSize="L"
					placeholder={t('inputPlaceholder')}
				/>
				<ButtonPrimary onClick={onSubmit} size="L">
					{t('saveButton')}
				</ButtonPrimary>
			</ModalWrapper>
		</Modal>
	);
};
